import PropTypes from 'prop-types'
import { Table, Form, InputGroup, Button } from 'react-bootstrap'
import { useState, useMemo, useEffect } from 'react'

// Formatear monto con separadores de miles
const formatMonto = monto => {
    if (!monto && monto !== 0) return ''
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(monto)
}

const GuiasRutaTable = ({ guias, loading }) => {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const pageSize = 10

    // Filtrar por guía
    const filteredGuias = useMemo(() => {
        if (!search) return guias
        return guias.filter(g => g.guia && g.guia.toLowerCase().includes(search.toLowerCase()))
    }, [guias, search])

    // Paginación
    const totalPages = Math.ceil((filteredGuias?.length || 0) / pageSize) || 1
    const pagedGuias = useMemo(() => {
        const start = (page - 1) * pageSize
        return filteredGuias.slice(start, start + pageSize)
    }, [filteredGuias, page])

    // Reset page if search or guias change
    useEffect(() => {
        setPage(1)
    }, [search, guias])

    return (
        <div>
            <InputGroup className="mb-2" size="sm" style={{ maxWidth: 300 }}>
                <Form.Control
                    placeholder="Buscar por guía..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    disabled={loading}
                />
                {search && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearch('')} disabled={loading}>
                        Limpiar
                    </Button>
                )}
            </InputGroup>
            <div style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto' }}>
                <Table striped bordered hover size="sm" style={{ minWidth: '600px', marginBottom: 0 }}>
                    <thead>
                        <tr>
                            <th>Guía</th>
                            <th>Cliente</th>
                            <th>Precio</th>
                            <th>Fecha creación</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="text-center">
                                    <span
                                        className="d-flex justify-content-center align-items-center"
                                        style={{ height: 60 }}>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"></span>
                                        Cargando guías...
                                    </span>
                                </td>
                            </tr>
                        ) : pagedGuias && pagedGuias.length > 0 ? (
                            pagedGuias.map(guia => (
                                <tr key={guia.id}>
                                    <td>{guia.guia}</td>
                                    <td>{guia.cliente?.cliente || 'N/A'}</td>
                                    <td>{formatMonto(guia.precio_tarifario?.precio) || 0}</td>
                                    <td>{guia.createdDate ? guia.createdDate.substring(0, 10) : ''}</td>
                                    <td>{guia.sub_estado?.sub_estado || ''}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="text-center">
                                    No hay guías para esta ruta
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {/* Paginación */}
            <div className="d-flex justify-content-between align-items-center mt-2" style={{ maxWidth: 400 }}>
                <Button variant="outline-primary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>
                    {'<<'}
                </Button>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}>
                    {'<'}
                </Button>
                <span style={{ minWidth: 80, textAlign: 'center' }}>
                    Página <strong>{page}</strong> de <strong>{totalPages}</strong>
                </span>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}>
                    {'>'}
                </Button>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}>
                    {'>>'}
                </Button>
                <span style={{ minWidth: 80, textAlign: 'center' }}>
                    Total de guías: <strong>{filteredGuias.length}</strong>
                </span>
            </div>
        </div>
    )
}

GuiasRutaTable.propTypes = {
    guias: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            guia: PropTypes.string.isRequired,
            precio: PropTypes.number,
            createdDate: PropTypes.string,
            sub_estado: PropTypes.shape({
                sub_estado: PropTypes.string
            }),
            cliente: PropTypes.object
        })
    ),

    loading: PropTypes.bool
}

export default GuiasRutaTable
